import { IPriceBookItem } from 'src/common/interfaces/price-book.interfaces';
import i18n from 'i18next';
import translations from 'src/internationalisation/translation-map.json';
import { CostType, QuantityType } from 'src/enums/cost-setup';
import ExcelJS from 'exceljs';
import type { Cell, Row } from 'exceljs';
import { toast } from '@oliasoft-open-source/react-ui-library';
import { writeExcelFile } from 'src/common/write-excel-file/write-excel-file';
import { PriceBookType } from 'src/enums/price-book';

interface ICostCodesOptions {
  value: string | null;
  label: string;
}

export const headings = [
  PriceBookType.Name,
  PriceBookType.CostCodesId,
  PriceBookType.Vendor,
  PriceBookType.Price,
  PriceBookType.Currency,
  PriceBookType.PriceType,
];

const headerTranslationsMap: { [key: string]: string } = {
  name: i18n.t(translations.name),
  costCodesId: i18n.t(translations.priceBook_itemCode),
  vendor: i18n.t(translations.priceBook_vendor),
  price: i18n.t(translations.priceBook_price),
  currency: i18n.t(translations.currency),
  priceType: i18n.t(translations.priceBook_priceType),
};

export const typeList = [
  { label: i18n.t(translations.costSetup_dayRate), value: CostType.DayRate },
  { label: i18n.t(translations.costSetup_lumpSum), value: CostType.LumpSum },
  {
    label: `${i18n.t(translations.costSetup_quantity)}: ${i18n.t(translations.costSetup_volume)}`,
    value: `${CostType.Quantity}-${QuantityType.Volume}`,
  },
  {
    label: `${i18n.t(translations.costSetup_quantity)}: ${i18n.t(translations.costSetup_length)}`,
    value: `${CostType.Quantity}-${QuantityType.Length}`,
  },
  {
    label: `${i18n.t(translations.costSetup_quantity)}: ${i18n.t(translations.costSetup_itemCount)}`,
    value: `${CostType.Quantity}-${QuantityType.ItemCount}`,
  },
];
export const filterAndSortDataRows = (
  dataRows: IPriceBookItem[],
  filters: IPriceBookItem | Record<string, never>,
  sorts: { [key in keyof IPriceBookItem]?: string },
  costCodesIdToCodeMap: Map<string | null, string>,
) =>
  dataRows
    .filter((row) =>
      (Object.keys(filters) as (keyof typeof filters)[]).every((key) => {
        const filterValue = key === PriceBookType.CostCodesId ? costCodesIdToCodeMap.get(row[key]) : row[key];
        return filters[key] === ''
          ? true
          : filterValue
              ?.toString()
              .toLowerCase()
              .includes((filters[key] || '').toString().toLowerCase());
      }),
    )
    .sort((a, b) => {
      for (const [key, value] of Object.entries(sorts)) {
        const keyTyped = key as keyof IPriceBookItem;
        switch (value) {
          case 'up': {
            return typeof a[keyTyped] === 'string' && typeof b[keyTyped] === 'string'
              ? (a[keyTyped] as string)?.localeCompare(b[keyTyped] as string)
              : (a[keyTyped] as number) - (b[keyTyped] as number);
          }
          case 'down': {
            return typeof a[keyTyped] === 'string' && typeof b[keyTyped] === 'string'
              ? (b[keyTyped] as string)?.localeCompare(a[keyTyped] as string)
              : (b[keyTyped] as number) - (a[keyTyped] as number);
          }
          default:
            return 0;
        }
      }
      return 0;
    });

export const dataHeaders = (
  dataRowsKeys: string[],
  filters: IPriceBookItem | Record<string, never>,
  setFilters: (args: IPriceBookItem) => void,
  sorts: { [key in keyof IPriceBookItem]?: string },
  setSorts: (args: { [key in keyof IPriceBookItem]?: string }) => void,
) => {
  const dataSortCells = dataRowsKeys.map((key) => {
    const keyTyped = key as keyof IPriceBookItem;
    const sort = Object.keys(sorts).includes(key) ? sorts[keyTyped] : '';
    const prettifyHeaderValue = headerTranslationsMap[keyTyped];
    return {
      key,
      value: prettifyHeaderValue,
      hasSort: true,
      sort,
      onSort: () => {
        const newSort = sort === 'up' ? 'down' : 'up';
        setSorts({
          [keyTyped]: newSort,
        });
      },
    };
  });
  const dataFilterCells = dataRowsKeys.map((key) => {
    const keyTyped = key as keyof IPriceBookItem;
    const filterValue = Object.keys(filters).includes(key) ? filters[keyTyped] : '';
    return {
      key,
      value: filterValue,
      type: 'Input',
      placeholder: 'Filter',
      onChange: (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
        setFilters({
          ...filters,
          [keyTyped]: ev.target.value,
        } as IPriceBookItem),
    };
  });
  return {
    dataSortCells,
    dataFilterCells,
  };
};

const borderStyle = { style: 'thin', color: { argb: 'D9D9D9' } };

const colorHeaderRow = (row: any) =>
  row.eachCell((cell: any) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'E2E2DC' },
    };
    cell.border = {
      top: borderStyle,
      left: borderStyle,
      bottom: borderStyle,
      right: borderStyle,
    };
  });

export const downloadPriceBookExcel = ({
  priceBook,
  currenciesList,
  costCodesItemsOptions,
  costCodesIdToCodeMap,
}: {
  priceBook: IPriceBookItem[];
  currenciesList: any[];
  costCodesItemsOptions: ICostCodesOptions[];
  costCodesIdToCodeMap: Map<string | null, string>;
}) => {
  const workBook = new ExcelJS.Workbook();
  const workSheet = workBook.addWorksheet(i18n.t(translations.priceBook));
  const helperSheet = workBook.addWorksheet('helper');

  // add cost codes item list to helper sheet
  helperSheet.getColumn(2).values = costCodesItemsOptions.map((item) => item.label);

  // add type list to helper sheet
  helperSheet.getColumn(7).values = typeList.map((item) => item.label);

  // add currency list to helper sheet
  helperSheet.getColumn(6).values = currenciesList.map((item) => item.label);

  workSheet.addRow(headings.map((header) => headerTranslationsMap[header]));
  colorHeaderRow(workSheet.lastRow);

  const getCells = (row: any) => {
    return Object.keys(row)
      .slice(3) // don't import ids
      .map((key) => {
        if (key === PriceBookType.PriceType) {
          return typeList.find((item) => item.value === row[key])?.label || '';
        } else if (key === PriceBookType.CostCodesId) {
          return costCodesIdToCodeMap.get(row[key]) || row[key] || '';
        } else {
          return row[key] || '';
        }
      });
  };
  priceBook.forEach((priceItem) => {
    workSheet.addRow(getCells(priceItem));
  });
  workSheet.eachRow((row, rowNumber) => {
    if (rowNumber > 1) {
      row.eachCell((cell, colNumber) => {
        if (colNumber === 6) {
          cell.dataValidation = {
            type: 'list',
            formulae: [`helper!$F$1:$F$${typeList.length}`],
            operator: 'equal',
            showErrorMessage: true,
          };
        }
        if (colNumber === 5) {
          cell.dataValidation = {
            type: 'list',
            formulae: [`helper!$E$1:$E${currenciesList?.length}`],
            operator: 'equal',
            showErrorMessage: true,
          };
        }
        if (colNumber === 2) {
          cell.dataValidation = {
            type: 'list',
            formulae: [`helper!$B$1:$B${costCodesItemsOptions?.length}`],
            operator: 'equal',
            showErrorMessage: true,
          };
        }
      });
    }
  });
  writeExcelFile(workBook, 'price-book');
};

const getCellValue = (cell: Cell): string | null => {
  const { value } = cell ?? {};
  if (value && typeof value === 'object' && 'richText' in value && Array.isArray(value.richText)) {
    return value.richText
      .map((part) => part.text)
      .join('')
      .trim();
  }
  return cell.text?.trim() || null;
};

export const uploadPriceBookExcel = async (
  uploadPriceBook: (priceBookId: string, data: IPriceBookItem[]) => void,
  priceBookId: string,
  costCodesCodeToIdMap: Map<string, string | null>,
) => {
  const input = document.createElement('input');
  input.type = 'file';

  input.onchange = (fileEvent: any) => {
    const file = fileEvent.target?.files[0];
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);

    reader.onload = async (readEvent) => {
      const data = readEvent.target?.result;
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(data as ArrayBuffer);
      const worksheet = workbook.worksheets[0];
      const priceList: any[] = [];
      worksheet.eachRow((row: Row) => {
        const values = row.values as (number | string | null | undefined)[];
        if (values) {
          values[1] = getCellValue(row.getCell(1));
          values[2] = values[2] ? costCodesCodeToIdMap.get(values[2] as string) || null : null;
          const cellValue = row.getCell(4)?.value;
          values[4] = typeof cellValue === 'number' ? cellValue : 0;
          priceList.push(values);
        }
      });
      const parsedPriceBook = priceList.slice(1).map((price) => {
        const importedItem = headings.reduce((acc, item, index) => {
          const priceValue =
            item === PriceBookType.PriceType
              ? typeList.find((typeItem) => typeItem.label === price[index + 1])?.value
              : price[index + 1];
          return { ...acc, [item]: priceValue };
        }, {});
        return importedItem;
      });
      if (parsedPriceBook.length === 0) {
        toast({
          message: {
            type: 'Error',
            content: 'No price book items',
          },
        });
      } else {
        uploadPriceBook(priceBookId, parsedPriceBook as IPriceBookItem[]);
      }
    };
  };
  input.click();
};
